import React from "react"
import {
  useReadLegalEntityPreferencesQuery,
  ViewType,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgEdit02 } from "@digits-shared/components/SVGIcons/line/Edit02.svg"
import { SvgEye } from "@digits-shared/components/SVGIcons/line/Eye.svg"
import { HeaderToggles } from "@digits-shared/DesignSystem/Containers/Headers/HeaderToggles"
import {
  type ToggleButtonsProps,
  ToggleOption,
} from "@digits-shared/DesignSystem/FormElements/ToggleButtons"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { useUserSettings } from "src/frontend/hooks/useUserSettings"
import type FrontendSession from "src/frontend/session"
import { userPreferenceKeys } from "src/shared/config/userPreferenceKeys"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

interface Props {
  Toggle?: React.FunctionComponent<ToggleButtonsProps<string>>
  onExitEditMode?: () => void
}

export const ViewEditToggle: React.FC<Props> = ({ onExitEditMode, Toggle = HeaderToggles }) => {
  const isPeriodBeforeCutover = useIsPeriodBeforeCutover()
  const { hasAccessToAspect } = useSession<FrontendSession>()
  const hasCategoryManager = hasAccessToAspect(AspectCode.CategoryManager)
  const isNotEditable = isPeriodBeforeCutover && !hasCategoryManager
  const { viewType } = useViewVersion()

  const {
    settings: { editLedgerEnabled },
    toggleUserSetting,
  } = useUserSettings()

  const onButtonClick = React.useCallback(
    (enabled: boolean) => {
      setTimeout(() => {
        toggleUserSetting(userPreferenceKeys.editLedger, enabled)

        if (!enabled) {
          onExitEditMode?.()
        }
      }, 350)
    },
    [onExitEditMode, toggleUserSetting]
  )

  React.useEffect(() => {
    if (isNotEditable && editLedgerEnabled) {
      onButtonClick(false)
    }
  }, [editLedgerEnabled, isNotEditable, onButtonClick])

  if (viewType !== ViewType.AIBookkeeper) {
    return null
  }

  return (
    <Toggle
      defaultValue={String(editLedgerEnabled)}
      onChange={(v: string) => onButtonClick(v === "true")}
    >
      <DigitsTooltip content="View Mode">
        <ToggleOption value="false">
          <SvgEye />
          View
        </ToggleOption>
      </DigitsTooltip>
      <DigitsTooltip
        content={
          isNotEditable
            ? "Editing is unavailable for this legal entity until the cutover date."
            : "Edit Mode"
        }
      >
        <ToggleOption value="true" disabled={isNotEditable}>
          <SvgEdit02 />
          Edit
        </ToggleOption>
      </DigitsTooltip>
    </Toggle>
  )
}

function useIsPeriodBeforeCutover() {
  const intervalOrigin = useIntervalOrigin()
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const { data, loading } = useReadLegalEntityPreferencesQuery({
    variables: {
      legalEntityId,
    },
  })

  return React.useMemo(() => {
    const cutoverTimestamp = data?.readLegalEntityPreferences?.bookkeeperAiCutoverAt

    if (loading || !cutoverTimestamp) {
      return false
    }

    const endOfInterval = dateTimeHelper.endOfIntervalOrigin(intervalOrigin)
    return dateTimeHelper.utcDayjsFromTimestamp(cutoverTimestamp).isSameOrAfter(endOfInterval)
  }, [data, intervalOrigin, loading])
}
